import { useState } from 'react';
import { ChevronDown, FileArchive, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

const PARQUET_EXT = /\.parquet$/i;
const PARQUET_MIME = 'application/vnd.apache.parquet';

const isParquet = (file: File): boolean =>
  PARQUET_EXT.test(file.name) || file.type === PARQUET_MIME;

// ---------------------------------------------------------------------------
// Collection-page "How it works" guide
// ---------------------------------------------------------------------------
// Audience: non-technical customer uploading a dataset for the first time.
// Goal: set expectations up-front about (a) what formats we accept, (b) what
// happens after upload, (c) why uploading a Parquet file is the fast path for
// large datasets. We deliberately do not mention multipart / presigned URLs /
// schema inference internals — those are our problem, not the customer's.
// ---------------------------------------------------------------------------

interface GuideStep {
  n: number;
  title: string;
  body: string;
  tone?: 'default' | 'skipped';
}

// CSV / Excel: steps 1 → 5 in order.
// Parquet: step 1 → step 5 (skips 2, 3, 4).
const GUIDE_STEPS: GuideStep[] = [
  {
    n: 1,
    title: 'Upload source file',
    body: 'Drop in a CSV, Excel, or Parquet file. Upload runs in your browser so you can keep working.',
  },
  {
    n: 2,
    title: 'Create a dataset',
    body: 'Pick one or more uploaded files and group them into a dataset for refinement.',
    tone: 'skipped',
  },
  {
    n: 3,
    title: 'Resolve data issues',
    body: 'Review columns, fix header mismatches, handle missing values, and confirm types.',
    tone: 'skipped',
  },
  {
    n: 4,
    title: 'Clean up the data',
    body: 'Apply final transformations so the dataset is consistent and query-friendly.',
    tone: 'skipped',
  },
  {
    n: 5,
    title: 'Ready to query',
    body: 'Ask questions about your dataset in plain English.',
  },
];

export const StructuredUploadGuide = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className='rounded-md border bg-muted/30'>
        <CollapsibleTrigger
          id='collection-page-how-it-works-toggle'
          className='flex w-full items-center justify-between p-3 text-sm font-medium hover:bg-muted/50 transition-colors'
        >
          <span>How uploads work</span>
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className='px-3 pb-3'>
          <div className='flex flex-col gap-3 pt-1'>
            <div className='flex flex-wrap gap-2'>
              {GUIDE_STEPS.map((step) => (
                <div
                  key={step.n}
                  className={cn(
                    'flex-1 min-w-[160px] rounded-md border p-3 flex flex-col gap-1.5',
                    step.tone === 'skipped'
                      ? 'border-dashed border-border bg-background opacity-70'
                      : 'border-border bg-background',
                  )}
                >
                  <div className='flex items-center gap-2'>
                    <span
                      className={cn(
                        'inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-medium',
                        'bg-muted text-muted-foreground',
                      )}
                    >
                      {step.n}
                    </span>
                    <span className='text-sm font-medium'>{step.title}</span>
                  </div>
                  <p className='text-xs text-muted-foreground leading-snug'>
                    {step.body}
                  </p>
                  {step.tone === 'skipped' && (
                    <span className='mt-1 inline-flex items-center self-start rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'>
                      Parquet skips this
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className='rounded-md border bg-background p-3 text-xs text-muted-foreground'>
              <span className='font-medium text-foreground'>
                Working with a very large dataset?
              </span>{' '}
              Export it as a Parquet file from your tool of choice (Excel,
              pandas, Tableau, BigQuery, and most data warehouses can do this).
              Parquet uploads go straight from step 1 to step 5 — no dataset
              creation, no refinement, no cleanup — which avoids the size limits
              that apply to CSV and Excel and gets you to querying much faster.
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// Upload-modal Parquet callout
// ---------------------------------------------------------------------------
// Rendered only when a .parquet file is staged in the dropzone. Confirms the
// customer's intuition — "I picked the right format, this will be fast" —
// without going into implementation detail.
// ---------------------------------------------------------------------------

interface StructuredUploadParquetCalloutProps {
  selectedFiles: File[];
}

export const StructuredUploadParquetCallout = ({
  selectedFiles,
}: StructuredUploadParquetCalloutProps) => {
  const parquetFiles = selectedFiles.filter(isParquet);
  if (parquetFiles.length === 0) return null;

  return (
    <div
      id='data-sources-parquet-detected-callout'
      className='flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 p-3 text-sm'
    >
      <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-200 dark:bg-amber-900'>
        <Zap className='h-4 w-4 text-amber-900 dark:text-amber-100' />
      </div>
      <div className='flex flex-col gap-1 min-w-0'>
        <div className='flex items-center gap-2 font-medium'>
          <FileArchive className='h-4 w-4' />
          <span>
            Parquet file detected
            {parquetFiles.length > 1 ? ` (${parquetFiles.length} files)` : ''}
          </span>
        </div>
        <p className='text-xs text-muted-foreground'>
          Parquet is the fast path for large datasets. This file will skip the
          refinement step and be ready to query as soon as the upload finishes.
        </p>
      </div>
    </div>
  );
};
